<tp-page-scaffold [pageAvailability]="availability$ | async">
  @if (cluster$ | async; as cluster) {
    <article>
      <div class="section-title-row">
        <h2 style="width: fit-content">{{ cluster.id }}</h2>
        <div class="flex-spacer"></div>
        <tp-button [matMenuTriggerFor]="actionsMenu" buttonStyle="secondary stroke" buttonId="actionsMenu">
          Actions <i class="fa-light fa-chevron-down"></i>
        </tp-button>
      </div>
      <div class="status-subtitle">
        <aside class="tp-liveness-indicator" [matTooltip]="clusterStatusTooltip(cluster)">
          <tp-status-indicator [class]="clusterIcon(cluster)" [status]="clusterIcon(cluster)"/>
          <p [class]="clusterIcon(cluster)">{{ cluster.status | clusterStatus }}</p>
        </aside>
      </div>

      <div class="tp-bento-container col">
        @if (clusterAutoPauseInformation$ | async; as autoPauseInformation) {
          @if (autoPauseInformation.autoPaused) {
            <div class="tp-bento-box subtle notif-bar">
              <h3><i class="fa-solid fa-circle-info"></i> We've paused this cluster</h3>
              <p>
                To optimize cloud resources, we automatically pause free-tier clusters after 3 days of inactivity.
                <br/>
                You can prevent future automatic pausing by upgrading to a scaled cluster.
              </p>
              <div class="notif-buttons">
                <tp-button buttonStyle="primary-outline green stroke" [height]="32" buttonId="resume" (click)="openPauseResumeModal('resume')">
                  Resume cluster
                </tp-button>
              </div>
            </div>
          } @else if (autoPauseInformation.pauseDueAt) {
            <div class="tp-bento-box subtle notif-bar">
              <h3><i class="fa-solid fa-triangle-exclamation"></i> This cluster is inactive</h3>
              <p>
                This free cluster has been inactive since {{ autoPauseInformation.inactiveSince | date: "d MMM yyyy" }}.
                <br/>
                To optimize cloud resources, we will automatically pause this cluster after 3 days of inactivity at <strong>{{ autoPauseInformation.pauseDueAt | date: "d MMM yyyy, HH:mm" }}</strong>.
                <br/>
                You can prevent future automatic pausing by upgrading to a scaled cluster.
              </p>
              <div class="notif-buttons">
                <tp-button [enabled]="!(markActiveSubmitting$ | async)" buttonStyle="primary-outline green stroke" [height]="32" buttonId="markActive" (click)="markClusterAsActive()">
                  @if (markActiveSubmitting$ | async) {
                    <tp-spinner/>
                  } @else {
                    Mark active
                  }
                </tp-button>
              </div>
            </div>
          }
        }

        @if (displayClusterPausedNotice$ | async) {
          <div class="tp-bento-box subtle notif-bar">
            <h3><i class="fa-solid fa-circle-info"></i> This cluster is paused</h3>
            <p>Resume your cluster to continue where you left off.</p>
            <div class="notif-buttons">
              <tp-button buttonStyle="primary-outline green stroke" [height]="32" buttonId="resume" (click)="openPauseResumeModal('resume')">
                Resume cluster
              </tp-button>
            </div>
          </div>
        }

        @if ((typeDBUpgradeAvailable$ | async) && !typeDBVersionUpdateTriggered) {
          <div class="tp-bento-box subtle notif-bar">
            @if (typeDBUpgradeRequiresMigration$ | async) {
              <h3><i class="fa-solid fa-triangle-exclamation"></i> Update available</h3>
              <p>
                Your cluster is ready to be updated to TypeDB {{ latestCompatibleTypeDBVersion$ | async }}
                <br/>
                {{ migrationWarning }}
              </p>
            } @else {
              <h3>Update available</h3>
              <p>Your cluster is ready to be updated to TypeDB {{ latestCompatibleTypeDBVersion$ | async }}</p>
            }
            <div class="notif-buttons">
              <tp-button
                buttonId="updateToLatestCompatibleTypeDB" [height]="32"
                buttonStyle="primary-outline green stroke" (click)="openUpdateTypeDBModal()"
              >
                Update to {{ latestCompatibleTypeDBVersion$ | async }}
              </tp-button>
              <a
                [href]="'https://github.com/typedb/typedb/releases/tag/' + (latestCompatibleTypeDBVersion$ | async)"
                target="_blank"
              >
                <tp-button buttonId="viewChangelog" buttonStyle="primary-outline white grey-stroke" [height]="32">
                  View changelog <i class="fa-light fa-arrow-up-right-from-square"></i>
                </tp-button>
              </a>
            </div>
          </div>
        }

        @if (clusterStarting$ | async) {
          <div class="tp-bento-box subtle notif-bar shimmer">
            <h3><i class="fa-solid fa-circle-info"></i> Cluster starting</h3>
            <p>This cluster is starting up and will be available in a few minutes.</p>
            <p>You'll be notified when it's ready.</p>
          </div>
        }

        <div class="tp-bento-container row">
          <div class="tp-bento-container row cluster-info">
            <div class="tp-bento-box subtle with-bottom-bar">
              <h3>Cluster info</h3>
              <table class="tp-info-table">
                <tr>
                  <th>Provider</th>
                  <td>{{ cluster.provider.name }}</td>
                </tr>
                <tr>
                  <th>Region</th>
                  <td>{{ cluster.region | region:"long" }}</td>
                </tr>
                <tr>
                  <th>Machine Type</th>
                  <td>
                    {{ cluster.machineType | machineType }}
                    @if (cluster.machineType.isBurstable) {
                      <i [matTooltip]="burstableTooltip" class="burstable-indicator fa-solid fa-circle-info"></i>
                    }
                  </td>
                </tr>
                <tr>
                  <th>Storage Capacity</th>
                  <td>{{ cluster.storageGB | storageSize }}</td>
                </tr>
                <tr>
                  <th>TypeDB Version</th>
                  <td>{{ cluster.typeDBVersion.version }}</td>
                </tr>
                <tr>
                  <th>Type</th>
                  <td><span [class.tooltip]="cluster.isFree" [matTooltip]="cluster.isFree ? 'Upgrade to access backups, more powerful machines, and increased storage capacity' : ''">{{ cluster.isFree ? 'Free' : 'Scaled' }}</span></td>
                </tr>
                <tr>
                  <th>Created By</th>
                  <td>
                    @if (clusterCreatedByUser$ | async; as user) {
                      <span>User: <a [routerLink]="memberDetailsPath(user, team)">{{ user.id }}</a></span>
                    }
                    @if (clusterCreatedByAPIToken$ | async; as token) {
                      <span>API Token: <a [routerLink]="teamSettingsPath(team)" fragment="api-tokens">{{ token.name }}</a></span>
                    }
                  </td>
                </tr>
              </table>
              <div class="flex-spacer"></div>
              <button class="tp-bento-bottom-bar hoverable" (click)="openEditModal()">Reconfigure cluster</button>
            </div>
          </div>
          <div class="tp-bento-container col connection-btns">
            @if (readyStudioLink$ | async; as studioLink) {
              <a [href]="studioLink" target="_blank">
                <button
                  class="tp-bento-box subtle hoverable tp-studio-connect"
                  [matTooltip]="(studioDisabledReason$ | async) || ''"
                  [disabled]="!(connectEnabled$ | async) || (isTypeDBVersion3_2$ | async)"
                >
                  <i class="fa-thin fa-bolt fa-fw"></i>
                  <div class="connection-btn-content">
                    <h4>Connect instantly with TypeDB Studio</h4>
                    <p>Open TypeDB Studio to query through TypeDB's web GUI</p>
                  </div>
                  <div class="flex-spacer"></div>
                  <div class="logo">
                    <img src="/assets/logo/announcement-swirl.svg" alt=""/>
                  </div>
                </button>
              </a>
            } @else {
              <button
                class="tp-bento-box subtle hoverable tp-studio-connect"
                [matTooltip]="(studioDisabledReason$ | async) || ''"
                [disabled]="!(connectEnabled$ | async) || (isTypeDBVersion3_2$ | async)" (click)="openStudioConnectDialog()"
              >
                <i class="fa-thin fa-bolt fa-fw"></i>
                <div class="connection-btn-content">
                  @if (isTypeDBVersion3_2OrLater$ | async) {
                    <h4>Connect instantly with TypeDB Studio</h4>
                    <p>Open TypeDB Studio to query through TypeDB's web GUI</p>
                  } @else {
                    <h4>Connect with TypeDB Studio</h4>
                    <p>Query through TypeDB's GUI</p>
                  }
                </div>
                <div class="flex-spacer"></div>
                <div class="logo">
                  <img src="/assets/logo/announcement-swirl.svg" alt=""/>
                </div>
              </button>
            }
            <button
              class="tp-bento-box subtle hoverable"
              [matTooltip]="(cannotConnectReason$ | async) || ''"
              [disabled]="!(connectEnabled$ | async)" (click)="openConsoleConnectDialog()"
            >
              <i class="fa-thin fa-rectangle-terminal fa-fw"></i>
              <div class="connection-btn-content">
                <h4>Connect with TypeDB Console</h4>
                <p>Access from the command line</p>
              </div>
            </button>
            <button
              class="tp-bento-box subtle hoverable"
              [matTooltip]="(cannotConnectReason$ | async) || ''"
              [disabled]="!(connectEnabled$ | async)" (click)="openDriversConnectDialog()"
            >
              <i class="fa-thin fa-gear-complex-code fa-fw"></i>
              <div class="connection-btn-content">
                <h4>Connect with TypeDB Drivers</h4>
                <p>Connect from one of our language drivers</p>
              </div>
            </button>
            @if (isTypeDBVersion3_2OrLater$ | async) {
              <button
                class="tp-bento-box subtle hoverable"
                [matTooltip]="(cannotConnectReason$ | async) || ''"
                [disabled]="!(connectEnabled$ | async)" (click)="openHttpConnectDialog()"
              >
                <i class="fa-thin fa-network-wired fa-fw"></i>
                <div class="connection-btn-content">
                  <h4>Connect with TypeDB HTTP API</h4>
                  <p>Connect using our HTTP API</p>
                </div>
              </button>
            }
          </div>
        </div>
        <div class="tp-bento-container row">
          <div class="tp-bento-container servers-storage-container flex-1 col">
            <div class="tp-bento-box subtle servers-table-container flex-1">
              <h3>Servers</h3>
              <table class="tp-info-table">
                <tr>
                  <th>Availability</th>
                  <td>{{ availabilityText$ | async }}</td>
                </tr>
                <tr>
              </table>
              <div>
                <table mat-table [dataSource]="servers$" [trackBy]="trackServerByUUID">
                  <ng-container matColumnDef="address">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header>Address</th>
                    <td mat-cell *matCellDef="let server"><tp-table-copyable-text-cell [text]="server.address || 'Loading...'"/></td>
                  </ng-container>
                  <ng-container matColumnDef="serverStatus">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
                    <td mat-cell *matCellDef="let server">{{ server.status | serverStatus }}</td>
                  </ng-container>
                  <ng-container matColumnDef="status">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header></th>
                    <td mat-cell *matCellDef="let server"><tp-status-indicator [status]="serverIcon(server, dialogOpen)"/></td>
                  </ng-container>

                  <tr mat-header-row *matHeaderRowDef="serverColumns"></tr>
                  <tr mat-row class="table-row-clickable" *matRowDef="let server; columns: serverColumns" (click)="handleServerTableRowClick(server)"></tr>
                </table>
              </div>
            </div>
            <div class="tp-bento-box subtle flex-1 with-bottom-bar">
              <h3>Storage usage</h3>
              @for (serverStorageUsage of serverStorageUsages$ | async; track serverStorageUsage) {
                <table class="tp-info-table">
                  <th>{{ serverStorageUsage.storageUsage ?? 'Unknown' }}</th>
                  <td>{{ serverStorageUsage.storageCapacity }}</td>
                </table>
                <mat-progress-bar mode="determinate" [value]="serverStorageUsage.storageUsagePercent"></mat-progress-bar>
                <p>Total: {{ serverStorageUsage.storageUsagePercent }}%</p>
              }
              <div class="flex-spacer"></div>
              <button class="tp-bento-bottom-bar hoverable" (click)="openEditModal()">Add more storage</button>
            </div>
          </div>
          <div class="tp-bento-container col flex-1">
            <div class="tp-bento-box subtle flex-1" [class.with-bottom-bar]="!(clusterSupportsSnapshots$ | async) || (clusterIsFree$ | async)">
              <div class="section-title-row"><h3>Backups</h3></div>
              @if (!(clusterSupportsSnapshots$ | async)) {
                <p class="backups-unavailable-info">This feature is not available on this cluster. Deploy a new cluster to gain access.</p>
                <div class="flex-spacer"></div>
                <a class="tp-bento-bottom-bar hoverable" routerLink="../../../../clusters/deploy">Deploy cluster</a>
              } @else if (clusterIsFree$ | async) {
                <p class="backups-unavailable-info">This feature is not available on free clusters. Upgrade this cluster to gain access.</p>
                <div class="flex-spacer"></div>
                <button class="tp-bento-bottom-bar hoverable" (click)="openEditModal()">Upgrade cluster</button>
              } @else {
                <div class="title-row">
                  <h3>Configuration</h3>
                  <tp-button
                    buttonId="editBackupsConfig" buttonStyle="primary-outline white grey-stroke"
                    [enabled]="!!(hasAdminAccess$ | async)"
                    (click)="openEditModal()"
                  >Edit</tp-button>
                </div>
                <table class="tp-info-table">
                  <tr>
                    <th>Snapshot Frequency</th>
                    <td>{{ toSentenceCase(cluster.snapshotConfiguration.frequency!) }}</td>
                  </tr>
                  <tr>
                    <th>Snapshot Retention</th>
                    <td>{{ cluster.snapshotConfiguration.retentionDays }} days</td>
                  </tr>
                </table>
                <div class="flex-1"></div>
                <hr/>
                <div class="title-row">
                  <h3>Snapshots</h3>
                  <tp-button buttonStyle="primary-outline white grey-stroke" buttonId="takeSnapshot" (click)="takeSnapshot()" [enabled]="!!(hasAdminAccess$ | async)">
                    <i class="fa-light fa-square-plus"></i> Take snapshot now
                  </tp-button>
                </div>
                <div>
                  <table mat-table [dataSource]="snapshotsData" [trackBy]="trackSnapshotByID">
                    <ng-container matColumnDef="createdAt">
                      <th mat-header-cell *matHeaderCellDef mat-sort-header>Created</th>
                      <td mat-cell *matCellDef="let snapshot">{{ snapshot.createdAt | datetime }}</td>
                    </ng-container>
<!--                    <ng-container matColumnDef="restoreSize">-->
<!--                      <th mat-header-cell *matHeaderCellDef mat-sort-header>Restore Size</th>-->
<!--                      <td mat-cell *matCellDef="let snapshot">-->
<!--                        @if (bytesToString(snapshot.restoreSize); as restoreSize) {-->
<!--                          {{ restoreSize }}-->
<!--                        } @else {-->
<!--                          <tp-spinner [size]="16" style="width:min-content"/>-->
<!--                        }-->
<!--                      </td>-->
<!--                    </ng-container>-->
                    <ng-container matColumnDef="type">
                      <th mat-header-cell *matHeaderCellDef mat-sort-header>Type</th>
                      <td mat-cell *matCellDef="let snapshot">{{ snapshot.type }}</td>
                    </ng-container>
                    <ng-container matColumnDef="actions">
                      <th mat-header-cell *matHeaderCellDef></th>
                      <td mat-cell *matCellDef="let snapshot">
                        <button
                          [disabled]="!(hasAdminAccess$ | async)"
                          mat-fab aria-label="more"
                          [matMenuTriggerFor]="snapshotsPopupMenu" [matMenuTriggerData]="{ snapshot }"
                          (click)="$event.stopPropagation()"
                        >
                          <i class="fa-light fa-ellipsis"></i>
                        </button>
                      </td>
                    </ng-container>

                    <tr mat-header-row *matHeaderRowDef="snapshotColumns"></tr>
                    <tr mat-row *matRowDef="let snapshot; columns: snapshotColumns"></tr>
                  </table>
                  <mat-paginator #snapshotsPaginator [pageSizeOptions]="[5]"></mat-paginator>
                </div>
              }
            </div>
          </div>
        </div>
      </div>
    </article>
  }
</tp-page-scaffold>

<mat-menu class="cluster-menu" #actionsMenu="matMenu" xPosition="before">
  @if (isInPausableState$ | async) {
    <button mat-menu-item [matTooltip]="(cannotPauseReason$ | async) || ''" [disabled]="!(pauseEnabled$ | async)" (click)="openPauseResumeModal('pause')">
      <i class="fa-light fa-circle-pause btn-icon"></i>Pause
    </button>
  }
  @if (isInResumableState$ | async) {
    <button mat-menu-item [matTooltip]="(cannotResumeReason$ | async) || ''" [disabled]="!(resumeEnabled$ | async)" (click)="openPauseResumeModal('resume')">
      <i class="fa-light fa-circle-play btn-icon"></i>Resume
    </button>
  }
  <button mat-menu-item [matTooltip]="(cannotEditReason$ | async) || ''" [disabled]="!(editEnabled$ | async)" (click)="openEditModal()">
    <i class="fa-light fa-chevrons-up btn-icon"></i> Upgrade
  </button>
  <button mat-menu-item [matTooltip]="(cannotEditReason$ | async) || ''" [disabled]="!(editEnabled$ | async)" (click)="openRenameModal()">
    <i class="fa-light fa-pen-to-square btn-icon"></i>Rename
  </button>
  <button mat-menu-item [matTooltip]="(cannotResetUserPasswordReason$ | async) || ''" [disabled]="!(resetUserPasswordEnabled$ | async)" (click)="openResetUserPasswordDialog()">
    <i class="fa-light fa-key btn-icon"></i>Reset password
  </button>
  @if (ready$ | async) {
    <a mat-menu-item routerLink="logs">
      <i class="fa-light fa-file-lines btn-icon"></i>View logs
    </a>
  }
  <button mat-menu-item [matTooltip]="(cannotCloneReason$ | async) || ''" [disabled]="!(cloneEnabled$ | async)" (click)="cloneCluster()">
    <i class="fa-light fa-clone btn-icon"></i>Clone
  </button>
  <button mat-menu-item [matTooltip]="(cannotDestroyReason$ | async) || ''" [disabled]="!(destroyEnabled$ | async)" (click)="openDestroyModal()" class="dangerous">
    <i class="fa-light fa-trash-can btn-icon"></i>Destroy
  </button>
</mat-menu>

<mat-menu #snapshotsPopupMenu="matMenu">
  <ng-template matMenuContent let-snapshot="snapshot">
    <button mat-menu-item (click)="restoreCluster(snapshot)">
      <i class="fa-light fa-refresh btn-icon"></i>Restore
    </button>
    <button mat-menu-item class="dangerous" (click)="deleteSnapshot(snapshot)">
      <i class="fa-light fa-trash-can btn-icon"></i>Delete
    </button>
  </ng-template>
</mat-menu>
